// The playable Silt Stair lift yard: the sim-side setup the play shell drives.
//
// Everything here is data and composition over the real simulation modules —
// navigation, combat, companion AI and dialogue are imported, never re-implemented.

import candidatesJson from '../../../content/generated/companions/slice/slice-candidates.json' with { type: 'json' };
import hedgeBinderJson from '../../../content/generated/origins/hedge-binder.json' with { type: 'json' };
import siltStairJson from '../../../content/generated/zones/Z03/silt-stair.json' with { type: 'json' };
import {
  createCombat,
  type CombatActorInput,
  type CombatState,
  type AutonomousDecision,
} from '../../../packages/sim/src/combat/index.ts';
import { createCompanionDecision } from '../../../packages/sim/src/ai/index.ts';
import { createContract, type CompanionContract } from '../../../packages/sim/src/companions/contracts.ts';
import type { DialogueOption } from '../../../packages/sim/src/dialogue/index.ts';
import type { QuestState } from '../../../packages/sim/src/quests/graph.ts';
import {
  metricDistance,
  findPath,
  type NavigationMap,
  type NavPosition,
  type WalkableCell,
} from '../../../packages/sim/src/navigation/index.ts';
import { z03Beat } from '../../scenarios/art/z03-scene.ts';

export const CELL = 1000;
export const BEAT = 'beat:lift-yard';

export const tileToWorld = (x: number, y: number, layer = 0): NavPosition => ({ x: x * CELL, y: y * CELL, layer });

const beat = z03Beat(BEAT);
const propTiles = new Set(beat.props.map(prop => `${prop.tile[0]}:${prop.tile[1]}`));

/**
 * Walkability for the yard the art actually draws: every footprint cell except
 * the ones an authored prop stands on. Props block sight as well as footing,
 * so the winch drum and crate stacks break line of sight for attacks.
 */
export const LIFT_YARD_MAP: NavigationMap = {
  cellSize: CELL,
  layers: [{
    id: 0,
    cells: ((): WalkableCell[] => {
      const cells: WalkableCell[] = [];
      for (let x = 0; x < beat.footprintTiles.width; x += 1) {
        for (let y = 0; y < beat.footprintTiles.height; y += 1) {
          if (!propTiles.has(`${x}:${y}`)) cells.push({ x: x * CELL, y: y * CELL });
        }
      }
      return cells;
    })(),
  }],
  links: [],
};

export const PC_ID = 'person:pc';
export const COMPANION_ID = (candidatesJson as CandidateFile).reference
  .find(candidate => candidate.templateId === 'former-mortuary-guard')!.contract.companionId;

interface CandidateFile {
  reference: readonly {
    templateId: string;
    name: string;
    person: { presentationKey: string; current: { hp: number; maxHp: number } };
    contract: CompanionContract;
  }[];
}

const candidate = (candidatesJson as CandidateFile).reference.find(entry => entry.templateId === 'former-mortuary-guard')!;

export const COMPANION_NAME = candidate.name;

/** The generated contract, re-validated through the sim's own constructor. */
export const COMPANION_CONTRACT: CompanionContract = createContract({
  companionId: candidate.contract.companionId,
  temperament: candidate.contract.temperament,
  obligations: candidate.contract.obligations,
  boundaries: candidate.contract.boundaries,
  preferences: candidate.contract.preferences,
});

/** How close you must come before the bailiffs treat you as their business. */
export const APPROACH_RANGE = 4000;

export const BAILIFF_IDS =['person:bailiff-axe', 'person:tavren-noll'] as const;

const ACTORS: readonly CombatActorInput[] = [
  { id: PC_ID, role: 'pc', position: tileToWorld(2, 6), hp: 14, maxHp: 14, movePerTick: 500, attackDamage: 3, attackRange: 1000, attackWindupTicks: 3 },
  { id: COMPANION_ID, role: 'companion', position: tileToWorld(1, 5), hp: candidate.person.current.hp, maxHp: candidate.person.current.hp, movePerTick: 500, attackDamage: 3, attackRange: 1000, attackWindupTicks: 3 },
  { id: BAILIFF_IDS[0], role: 'enemy', position: tileToWorld(11, 3), hp: 6, maxHp: 6, movePerTick: 400, attackDamage: 2, attackRange: 1000, attackWindupTicks: 4 },
  { id: BAILIFF_IDS[1], role: 'enemy', position: tileToWorld(12, 6), hp: 7, maxHp: 7, movePerTick: 400, attackDamage: 2, attackRange: 1000, attackWindupTicks: 4 },
];

export const createYardCombat = (): CombatState => createCombat(LIFT_YARD_MAP, ACTORS, PC_ID);


/**
 * Enemy judgement plus the real companion adapter. Bailiffs close on the
 * nearest living non-enemy and strike when it is inside their reach; ties break
 * on actor id, so the whole decision function stays deterministic.
 */
export function createDecision(map: NavigationMap = LIFT_YARD_MAP): AutonomousDecision {
  const companion = createCompanionDecision(map, { [COMPANION_ID]: COMPANION_CONTRACT });
  return (state, actorId) => {
    const self = state.actors[actorId]!;
    if (self.role === 'companion') return companion(state, actorId);
    if (self.role !== 'enemy') return null;
    let targetId: string | null = null;
    let best = Number.MAX_SAFE_INTEGER;
    for (const id of Object.keys(state.actors).sort()) {
      const other = state.actors[id]!;
      if (other.role === 'enemy' || other.status === 'dead') continue;
      const distance = metricDistance(self.position, other.position);
      if (distance === null || distance >= best) continue;
      best = distance;
      targetId = id;
    }
    if (targetId === null) return null;
    // Danger is opted into: this bailiff holds until you close on them or
    // anyone draws first. Closing is per-bailiff; a first blow is the yard's.
    if (!drewFirst(state) && best > APPROACH_RANGE) return null;
    if (best <= self.attackRange) return { type: 'attack', targetId };
    const destination = state.actors[targetId]!.position;
    return findPath(map, self.position, destination) ? { type: 'move', destination: { ...destination } } : null;
  };
}

const origin = hedgeBinderJson as unknown as {
  title: string;
  personalGoal: { description: string };
  firstEncounter: { speaker: string; initialNpcDialogue: string; progressiveDisclosureReview: { immediateObjective: string } };
  dialogueOptions: readonly DialogueOption[];
};

/** Melian's conversation: the origin's authored options plus the zone's tutorial line. */
export const MELIAN_DIALOGUE: readonly DialogueOption[] = [
  ...origin.dialogueOptions,
  ...(siltStairJson as unknown as { tutorialDialogue: readonly DialogueOption[] }).tutorialDialogue
    .filter(option => option.prerequisites.all.every(fact => !fact.startsWith('location:'))),
];

/** The hedge binder arrives knowing their own craft, and having just arrived. */
export const OPENING_QUEST_STATE: QuestState = {
  facts: ['arrived:silt-stair', 'knowledge:reagent-distillation'],
  completedNodeIds: [],
};

/** The hedge binder argues chemistry well enough to carry the assay and the warning. */
export const PC_SOCIAL_ABILITY = 3;

/** A first blow on a bailiff makes the whole yard a fight. */
export function drewFirst(state: CombatState): boolean {
  return state.events.some(event => event.type === 'hit' && state.actors[event.targetId]?.role === 'enemy');
}

/**
 * Player-facing story line. The title card already names the place, so a narrator line
 * that only names it again ("Toll Stair: …" under TOLL STAIR) drops the prefix and reads
 * as narration. Living speech and every other narrator keep their speaker.
 */
export function narrationLine(
  speakerId: string,
  speaker: string,
  text: string,
  placeNames: readonly string[],
): string {
  if (speakerId === 'narrator' && placeNames.includes(speaker)) return text;
  return `${speaker}: ${text}`;
}
